import type { DetailContent } from './mppHelpers';
import { STAGE_COLORS } from './mppHelpers';
import styles from './DailyDashboard.module.css';

const formatLbs = (n: number) => Math.round(n).toLocaleString();
const formatInt = (n: number) => n.toLocaleString();

export function SankeyDetailPanel({
  content,
  onBack,
  onExpand,
}: {
  content: DetailContent;
  onBack: () => void;
  onExpand?: () => void;
}) {
  return (
    <div className={`${styles.card} ${styles.detailCard}`}>
      <div className={styles.cardHd}>
        <div className={styles.detailHeader}>
          <span
            className={styles.stageTag}
            style={{ background: content.stageColor }}
          >
            {content.stageLabel}
          </span>
          <strong className={styles.detailLabel}>{content.primaryLabel}</strong>
        </div>
        <div className={styles.cardHdActions}>
          {onExpand && (
            <button
              type="button"
              className={styles.expandBtn}
              onClick={onExpand}
              title="Expand"
            >
              ⤢
            </button>
          )}
          <button
            type="button"
            className={styles.backBtn}
            onClick={onBack}
            title="Back to overview"
          >
            ← Back
          </button>
        </div>
      </div>
      <div className={styles.cardSub} style={{ padding: '4px 12px 0' }}>
        {content.flowRows} flow rows · {content.ordersTouched} customer orders touched
      </div>

      <div className={styles.detailStats}>
        <div className={styles.detailStat}>
          <div className={styles.detailStatLbl}>Lbs in</div>
          <div className={styles.detailStatVal}>{formatLbs(content.lbsIn)}</div>
        </div>
        <div className={styles.detailStat}>
          <div className={styles.detailStatLbl}>Lbs out</div>
          <div className={styles.detailStatVal}>{formatLbs(content.lbsOut)}</div>
        </div>
        <div className={styles.detailStat}>
          <div className={styles.detailStatLbl}>Orders</div>
          <div className={styles.detailStatVal}>
            {formatInt(content.ordersTouched)}
          </div>
        </div>
      </div>

      <DetailSection title="Customer orders downstream" count={content.orders.length}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>AU #</th>
              <th>Customer</th>
              <th>SKU</th>
              <th>Lbs</th>
              <th>Day</th>
            </tr>
          </thead>
          <tbody>
            {content.orders.slice(0, 200).map((o, i) => (
              <tr key={i}>
                <td><code>{o.au || '—'}</code></td>
                <td>{o.customer}</td>
                <td title={o.sku}>{o.sku}</td>
                <td>{formatLbs(o.lbs)}</td>
                <td>{String(o.day)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailSection>

      <DetailSection title="Vendor PO chain" count={content.pos.length}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Vendor</th>
              <th>PO #</th>
              <th>Lands</th>
              <th>Conf</th>
              <th>Qty</th>
            </tr>
          </thead>
          <tbody>
            {content.pos.map((p, i) => (
              <tr key={i}>
                <td>{p.vendor}</td>
                <td><code>{p.poNumber}</code></td>
                <td>{p.lands}</td>
                <td>{p.conf}</td>
                <td>{p.qty ? formatLbs(p.qty) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailSection>

      <DetailSection title="Supply chain" count={content.supply.length}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Stage</th>
              <th>Material</th>
              <th>Lbs</th>
            </tr>
          </thead>
          <tbody>
            {content.supply.slice(0, 200).map((s, i) => (
              <tr key={i}>
                <td>
                  <span
                    className={styles.stageMini}
                    style={{ background: STAGE_COLORS[s.stage] ?? '#64748b' }}
                  >
                    {s.stage}
                  </span>
                </td>
                <td title={s.label}>{s.label}</td>
                <td>{formatLbs(s.lbs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailSection>

      <DetailSection title="By day" count={content.byDay.length}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>Day</th>
              <th>Lbs</th>
              <th>Flows</th>
            </tr>
          </thead>
          <tbody>
            {content.byDay.map((d, i) => (
              <tr key={i}>
                <td>Day {String(d.day)}</td>
                <td>{formatLbs(d.lbs)}</td>
                <td>{formatInt(d.flows)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DetailSection>
    </div>
  );
}

function DetailSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.detailSection}>
      <h4 className={styles.detailSectionTitle}>
        {title} <span className={styles.cntBadge}>{count}</span>
      </h4>
      <div className={styles.detailSectionScroll}>{children}</div>
    </div>
  );
}
